<template>
  <OverlayLoader :show="loadModal" />

  <div class="container-fluid px-4">
    <div class="mb-3">
      <BreadcrumbNav />
    </div>
    <!--h4 class="text-success text-center">Api integrated</h4-->
    <MaterialButton v-if="canAccessSetupWizard" class="mx-2" @click="openSetupWizardModal()">Setup Wizard</MaterialButton>
    <button class="btn-refresh mx-2" @click="refreshTable()"><i class="material-icons text-sm align-middle">refresh</i> Refresh</button>
    <BaseTable heading="Activities" :rows="users" :columns="columns" searchable :selectable="false" :loading="loading"
      :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
      @searchChanged="onSearchChanged">
      <template #actions="{ row }">
        <button v-if="canEditActivity(row.activityid)" class="btn btn-sm my-0 py-0" @click="editSkill(row.activityid)">
          <i class="material-icons text-lg text-orange" style="color: #e3c9;">edit</i>
        </button>
        <button v-else class="btn btn-sm my-0 py-0" @click="editSkill(row.activityid)">
          <i class="material-icons text-lg" style="color: #6c757d;">visibility</i>
        </button>
        <button v-if="canDeleteActivity(row.activityid)" class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
          <i class="material-icons text-lg text-danger" style="color: #FF0000;">delete</i>
        </button>
      </template>

      <template #col-overview="{ row }">
        <button class="btn btn-sm btn-outline-info my-0" @click="openSummary(row.activityid)">
          Overview
        </button>
      </template>

      <template #col-activitySummary="{ row }">
        <button class="btn btn-sm btn-outline-success my-0" @click="openActivitySummary(row.activityid, row.activityname)">
          <i class="material-icons text-sm align-middle">summarize</i> Summary
        </button>
      </template>

      <template #col-status="{ row }">
        <MaterialSelectedBadge :label="row.status" :color="row.status == 'active' ? 'info' : 'danger'" />
      </template>

      <template #col-setupstatus="{ row }">
        <button class="btn btn-link p-0 m-0" @click="openSetupStatus(row)" style="text-decoration: none;">
          <MaterialSelectedBadge :label="row.setupstatus"
            :color="row.setup_status?.setupcomplete ? 'success' : 'warning'" />
        </button>
      </template>
      <template #col-publicview="{ row }">
        <MaterialSelectedBadge :label="row.publicview" :color="row.publicview === 'Yes' ? 'success' : 'secondary'" />
      </template>

      <template #col-starttime="{ row }">
        {{ formatTime(row.starttime) }}
      </template>
    </BaseTable>

    <DeleteModal v-model:show="showDeleteModal" :itemName="selectedActivity?.activityname || 'this activity'" @confirm="deleteActivity" />

    <!-- Delete error safeguard -->
    <div v-if="deleteError" class="delete-error-overlay">
      <div class="delete-error-card">
        <div class="delete-error-header">
          <i class="material-icons">shield</i>
          <span>Deletion Safeguard</span>
          <button class="delete-error-close" @click="deleteError = null">&times;</button>
        </div>
        <div class="delete-error-body">
          <div class="delete-error-icon-wrap">
            <i class="material-icons">error_outline</i>
          </div>
          <p class="delete-error-msg">{{ deleteError }}</p>
          <div class="delete-error-help">
            <h6><i class="material-icons">lightbulb</i> What you need to do</h6>
            <ol>
              <li>Ensure the activity status is set to <strong>Inactive</strong> before attempting deletion.</li>
              <li>Remove or reassign any <strong>Workgroups</strong>, <strong>Schedules</strong>, or <strong>Instances</strong> linked to this activity.</li>
              <li>Once all dependencies are cleared, return here and delete the activity.</li>
            </ol>
            <p class="delete-error-note">
              <i class="material-icons" style="font-size:14px;vertical-align:middle;">info</i>
              This safeguard is in place to prevent accidental deletions that could affect workgroups, schedules, and assignments.
            </p>
          </div>
        </div>
        <div class="delete-error-footer">
          <button class="btn btn-sm btn-primary" @click="deleteError = null">Got it</button>
        </div>
      </div>
    </div>

    <ContentModal v-model="showModal" title="Activity Overview">
      <template #default>
        <Summary :tree="activityTreeData" />
      </template>
    </ContentModal>

    <SetupStatusModal v-model="showSetupStatusModal" :setupStatus="selectedSetupStatus" entityLabel="activity" />
    <SummaryModal v-model="showSummaryModal" :title="summaryTitle" :summaryList="summaryData" />
  </div>
  <Modal v-if="isSetupWizardModalOpen" :modalHeading="setupWizardModalHeading" :modalContent="setupWizardModalContent"
    :iframeUrl="setupWizardIframeUrl" size="xl" refresh-on-close @close="closeSetupWizardModal" />
</template>

<script setup>
import BaseTable from "@/components/Table/Table.vue";
import DeleteModal from "@/components/Modal/ConfirmationModal.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { getLocationId } from "../../utils/SessionDataHandler";
import MaterialSelectedBadge from "@/components/common/MaterialSelectedBadge.vue";
import { formatDate } from "../../utils/FormatDate";

// Format a time-only string like "10:00:00" into "10:00 AM"
function formatTime(timeStr) {
  if (!timeStr) return '-';
  // If it's already a full datetime string, fall back to formatDate
  if (timeStr.includes('T') || timeStr.includes('-')) return formatDate(timeStr);
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
}
import Summary from "./components/Summary.vue";
import OverlayLoader from "@/components/Loader/OverlayLoader.vue";
import { useRouter } from "vue-router";
import Modal from "@/components/Modal/Modal.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import SetupStatusModal from "@/components/Modal/SetupStatusModal.vue";
import SummaryModal from "@/components/Modal/SummaryModal.vue";
import { useToast } from "vue-toast-notification";
import { baseUrl } from "../../utils/BaseUrl.js";
import { usePermissions } from "@/composables/usePermissions";
import { useBreadcrumb } from "@/composables/useBreadcrumb";
import BreadcrumbNav from "@/components/common/BreadcrumbNav.vue";

const store = useStore();
const router = useRouter();
const toast = useToast();
const { canAccessSetupWizard, canEditActivity, canDeleteActivity } = usePermissions();
const { setTrail, clearTrail } = useBreadcrumb();

// Set top-level breadcrumb for this list page
clearTrail();
setTrail([
  { label: 'Manage Activities/Events', icon: 'event' },
]);

const showDeleteModal = ref(false);
const showModal = ref(false);
const selectedActivity = ref(null);
const activityTreeData = ref(null);
const deleteError = ref(null);

const showSetupStatusModal = ref(false);
const selectedSetupStatus = ref(null);

const openSetupStatus = (row) => {
  selectedSetupStatus.value = row.setup_status;
  showSetupStatusModal.value = true;
};

const showSummaryModal = ref(false);
const summaryData = ref([]);
const summaryTitle = ref('');

const openActivitySummary = async (activityId, activityName) => {
  summaryTitle.value = `Summary: ${activityName}`;
  loadModal.value = true;
  try {
    summaryData.value = await store.dispatch('fetchActivitySummary', activityId);
    showSummaryModal.value = true;
  } catch (e) {
    console.error('Error loading summary:', e);
  } finally {
    loadModal.value = false;
  }
};

const locationId = getLocationId();

const loading = ref(true);
const searchTerm = ref("");
const loadModal = ref(false);

const isSetupWizardModalOpen = ref(false);
const setupWizardModalHeading = ref("Setup Wizard");
const setupWizardModalContent = ref("");
const setupWizardIframeUrl = ref("");

// ✅ Get pagination from store
const pagination = computed(() => store.getters.activitiesByLocationPagination);

const columns = [
  { key: "activityid", label: "ID" },
  { key: "activityname", label: "ACTIVITY NAME" },
  { key: "setupstatus", label: "SETUP STATUS" },
  { key: "overview", label: "OVERVIEW" },
  { key: "activitySummary", label: "SUMMARY" },
  { key: "starttime", label: "START TIME" },
  { key: "duration", label: "DURATION" },
  { key: "schedule_offset", label: "SCHEDULE OFFSET" },
  { key: "priority", label: "PRIORITY" },
  { key: "autogenerate", label: "AUTO-GENERATE" },
  { key: "autoschedule", label: "AUTO-SCHEDULE (status)" },
  { key: "autosubstitute", label: "AUTO-SUBSTITUTE" },
  { key: "publicview", label: "PUBLICLY VIEWABLE" },
  { key: "status", label: "STATUS" },
];

const users = computed(() => {
  const apiData = store.getters.stActivitiesByLocation || [];
  return apiData.map((item) => ({
    ...item,
    setupstatus: item.setup_status?.setupcomplete ? "Completed" : "Pending",
    duration: `${item.duration} minutes`,
    schedule_offset: `${item.schedule_offset} days`,
    autogenerate: item.autogenerate ? "Yes" : "No",
    autoschedule: item.autoschedule ? "Enabled" : "Disabled",
    autosubstitute: item.autosubstitute ? "Yes" : "No",
    publicview: (
      item.publicview === true
      || item.publicview === 1
      || item.publicview === '1'
      || String(item.publicview || '').toLowerCase() === 'yes'
    ) ? "Yes" : "No",
  }));
});

// ✅ Fetch activities with pagination and search
const fetchActivities = async (page = 1, pageSize = 10) => {
  loading.value = true;
  try {
    // Use current pageSize from pagination if available
    const currentPageSize = pagination.value?.pageSize || pageSize;

    await store.dispatch("fetchActivitiesByLocation", {
      locationId,
      page,
      pageSize: currentPageSize,
    });
  } finally {
    loading.value = false;
  }
};

// 🧠 Initial load
onMounted(() => fetchActivities());

// ✅ Pagination handlers
const onChangePage = async (page) => {
  await fetchActivities(page);
};

const onUpdatePageSize = async (size) => {
  await fetchActivities(1, size);
};

// ✅ Search handler
const onSearchChanged = async (term) => {
  searchTerm.value = term;
  await fetchActivities(1);
};

// ✅ Other handlers
const openSummary = async (id) => {
  loadModal.value = true;
  activityTreeData.value = await store.dispatch('fetchActivityTree', id);
  loadModal.value = false;
  showModal.value = true;
};

const openDeleteModal = (row) => {
  if (row.status !== "inactive") {
    toast.warning("Please update the activity to inactive status before deleting.", { position: "top-right" });
    return;
  }
  selectedActivity.value = row;
  showDeleteModal.value = true;
};

const editSkill = async (id) => {
  try {
    const details = await store.dispatch("fetchActivityDetails", id);
    const activityName = details?.activityname || `Activity #${id}`;
    // Start a fresh breadcrumb trail: Activities list → this Activity
    setTrail([
      { label: 'Activities', icon: 'list', route: { name: 'activities-events' } },
      { label: activityName, icon: 'event', route: { name: 'editActivity', params: { locationid: locationId, activityid: id } } },
    ]);
    router.push({ name: "editActivity", params: { locationid: locationId, activityid: id } });
  } catch (error) {
    console.error("Edit activity:", error);
  }
};

const deleteActivity = async () => {
  if (!selectedActivity.value) return;

  try {
    const response = await store.dispatch("deleteActivity", {
      locationId,
      activityId: selectedActivity.value.activityid,
    });

    // Check response for success (returncode === 0 or "0" means success)
    const isSuccess = response && (response.returncode === 0 || response.returncode === "0");
    
    if (isSuccess) {
      const message = response?.message || "Activity deleted successfully";
      toast.success(message, { position: "top-right" });
    } else {
      const message = response?.message || "Failed to delete activity";
      toast.error(message, { position: "top-right" });
    }

    // Refresh the activities list
    await fetchActivities(pagination.value.currentPage || 1);
    
    // Close modal and reset selected activity
    showDeleteModal.value = false;
    selectedActivity.value = null;
  } catch (error) {
    console.error("Error deleting activity:", error);
    const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Failed to delete activity";
    const msgLower = errorMessage.toLowerCase();
    if (msgLower.includes('cannot delete') || msgLower.includes('assigned') || msgLower.includes('in use')) {
      deleteError.value = errorMessage;
    } else {
      toast.error(errorMessage, { position: "top-right" });
    }
    showDeleteModal.value = false;
    selectedActivity.value = null;
  }
};

const openSetupWizardModal = () => {
  setupWizardIframeUrl.value = `${baseUrl}/MF/cs-mf-setupwizard/`;
  setupWizardModalHeading.value = "Setup Wizard";
  isSetupWizardModalOpen.value = true;
};

const closeSetupWizardModal = () => {
  isSetupWizardModalOpen.value = false;
  setupWizardIframeUrl.value = "";
  refreshTable();
};

const refreshTable = () => {
  fetchActivities(pagination.value.currentPage || 1);
};
</script>

<style scoped>
.delete-error-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.45);
  animation: fadeIn .2s;
}
.delete-error-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.18);
  max-width: 480px;
  width: 92%;
  overflow: hidden;
  animation: slideUp .25s ease-out;
}
.delete-error-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #ef5350 0%, #c62828 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
}
.delete-error-header i { font-size: 22px; }
.delete-error-close {
  margin-left: auto;
  background: none;
  border: none;
  color: #fff;
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
  opacity: .8;
}
.delete-error-close:hover { opacity: 1; }
.delete-error-body {
  padding: 24px 20px 12px;
  text-align: center;
}
.delete-error-icon-wrap {
  margin-bottom: 12px;
}
.delete-error-icon-wrap i {
  font-size: 48px;
  color: #ef5350;
}
.delete-error-msg {
  font-size: 1.05rem;
  font-weight: 600;
  color: #344767;
  margin-bottom: 16px;
}
.delete-error-help {
  text-align: left;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 16px;
}
.delete-error-help h6 {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 10px;
  font-size: 0.88rem;
}
.delete-error-help h6 i { font-size: 18px; }
.delete-error-help ol {
  padding-left: 1.25rem;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: #495057;
}
.delete-error-help ol li {
  margin-bottom: 6px;
}
.delete-error-note {
  font-size: 0.78rem;
  color: #7b809a;
  margin: 0;
  padding: 8px 10px;
  background: #e8f4fd;
  border-radius: 6px;
  border-left: 3px solid #1976d2;
}
.delete-error-footer {
  padding: 12px 20px 16px;
  text-align: center;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.btn-refresh {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.btn-refresh:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.55);
}
.btn-refresh:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
}
</style>
